import styled from 'styled-components';

import {
  body2,
  colors,
  smallShadow,
  spacing,
  easing,
  duration,
  insetShadow,
  body2Medium,
  captionMedium,
  backgroundColors,
} from 'ui/theme';
import { StyledTooltipTrigger } from 'ui/tooltip/Tooltip.styled';

import { SEGMENTED_CONTROL_VARIANTS } from './types';

export const Wrapper = styled.div<{
  variant?: SEGMENTED_CONTROL_VARIANTS;
  isFullWidth?: boolean;
}>`
  ${body2}
  ${insetShadow}
  border-radius: ${spacing.threeEights};
  display: inline-flex;
  gap: ${spacing.quarter};
  height: fit-content;
  padding: ${spacing.quarter};
  background-color: ${({ variant }) =>
    variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT
      ? `rgba(255, 255, 255, 0.15)`
      : backgroundColors.backgroundSegmentedControl};
  ${({ isFullWidth }) =>
    isFullWidth
      ? `
    width: 100%;

    > ${StyledTooltipTrigger} {
      width: 100%;
    }
    `
      : ''}
`;

export const Option = styled.div<{
  isFullWidth?: boolean;
  optionMinWidth?: string;
  variant?: SEGMENTED_CONTROL_VARIANTS;
}>`
  ${body2Medium}
  align-items: center;
  border-radius: ${spacing.quarter};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  box-sizing: border-box;
  transition: all ${duration.short} ${easing.exit};
  padding: ${spacing.quarter} ${spacing.half};
  gap: ${spacing.quarter};
  color: ${({ variant }) =>
    variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT ? colors.white : colors.textSecondary};
  ${({ optionMinWidth }) => (optionMinWidth ? `min-width: ${optionMinWidth}` : '')};
  ${({ isFullWidth }) => (isFullWidth ? `width: 100%` : '')};

  svg {
    height: ${spacing.base};
    width: ${spacing.base};
  }

  &[data-size='small'] {
    ${captionMedium}
    height: ${spacing.plusHalf};
    svg {
      height: ${spacing.threeQuarter};
      width: ${spacing.threeQuarter};
    }
  }

  &[data-size='medium'] {
    height: ${spacing.plusThreeQuarter};
  }

  &[data-size='large'] {
    padding: ${spacing.threeEights} ${spacing.fiveEights};
    height: ${spacing.double};
    gap: ${spacing.half};
  }

  &:hover:not([aria-disabled='true']) {
    background-color: ${({ variant }) =>
      variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT
        ? `rgba(255, 255, 255, 0.15)`
        : colors.backgroundSegmentedControlHover};
    color: ${({ variant }) =>
      variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT ? colors.textOnColor : colors.textPrimary};
    transition: all ${duration.short} ${easing.entrance};

    svg[data-temporary-fix] path {
      fill: ${({ variant }) =>
        variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT
          ? colors.iconOnColor
          : colors.iconPrimaryHover};
      stroke: none;
    }
  }

  &[aria-disabled='true'][aria-checked] {
    cursor: not-allowed;
    color: ${colors.textDisabled};
    svg[data-temporary-fix] path {
      fill: ${colors.iconDisabled};
      stroke: none;
    }
  }

  &[aria-checked='true'] {
    background-color: ${colors.backgroundPrimary};
    color: ${colors.textPrimary};
    ${smallShadow}

    &:hover {
      background-color: ${colors.backgroundPrimary};
      color: ${colors.textPrimary};
      svg[data-temporary-fix] path {
        fill: ${colors.iconEmphasis};
        stroke: none;
      }
    }
  }

  &[aria-checked='false'] {
    color: ${({ variant }) =>
      variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT
        ? colors.textOnColor
        : colors.textSecondary};
    svg[data-temporary-fix] path {
      fill: ${({ variant }) =>
        variant === SEGMENTED_CONTROL_VARIANTS.TRANSLUCENT
          ? colors.iconOnColor
          : colors.iconPrimary};
      stroke: none;
    }
  }

  &:focus-visible:not([aria-disabled='true']) {
    outline: solid ${spacing.eighth} ${colors.purple50};
  }
`;

export const Name = styled.span`
  & + * {
    margin-left: ${spacing.half};
  }
`;
